import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, Scale, FileText, BookOpen } from 'lucide-react';

/**
 * Comprehensive disclaimers page. Linked from the footer of every page
 * and the top-nav. Designed to be auditable: each disclaimer has its own
 * card with a heading so it can be cited individually.
 */
const SECTIONS = [
  {
    id: 'workforce-development',
    icon: Shield,
    title: 'Workforce-Development Apprenticeship Disclaimer',
    body: (
      <>
        <p>
          <strong>MedCare Nursing Institute &amp; Training Academy is a workforce-development
          apprenticeship training program.</strong> All courses offered by MedCare are
          administered solely through and as a workforce-development apprenticeship
          training program, registered with the U.S. Department of Labor.
        </p>
        <p>
          MedCare is <strong>NOT</strong> certified, accredited, endorsed, or licensed
          by any State Board of Nursing or any State Department of Health.
          MedCare <strong>does NOT</strong> issue, administer, or proctor any state
          licensure examination, and MedCare <strong>does NOT</strong> act in concert
          with any State Board of Nursing or State Department of Health.
        </p>
        <p>
          Completion of any MedCare course does <strong>not by itself confer a state
          license</strong>. Students who wish to pursue state licensure must
          independently meet their state&apos;s separate requirements (NCLEX-PN /
          NCLEX-RN, state CNA registry, EMT National Registry, Phlebotomy
          certification, etc.) through the appropriate state agency.
        </p>
      </>
    ),
  },
  {
    id: 'governing-bodies',
    icon: Scale,
    title: 'Governing Bodies for MedCare Students &amp; Members',
    body: (
      <>
        <p>
          All MedCare students and all MedCare association members are
          <strong> governed solely by the MedCare National Board of Nursing and
          the Vital Health Sciences Association</strong>. These two divisions
          operate as internal credentialing bodies under MedCare&apos;s
          workforce-development authority.
        </p>
        <p>
          The MedCare National Board of Nursing and the Vital Health Sciences
          Association are NOT state boards of nursing, NOT state departments of
          health, and NOT state licensing authorities. They are internal,
          association-level credentialing bodies whose purpose is to register
          apprenticeship graduates, track continuing education, and maintain
          a verifiable member directory.
        </p>
      </>
    ),
  },
  {
    id: 'state-licensure',
    icon: AlertTriangle,
    title: 'State Licensure Notice',
    body: (
      <>
        <p>
          A MedCare certificate of completion is a record of training inside the
          MedCare apprenticeship program. It does <strong>not</strong> grant the
          right to practice as a CMA, LPN, RN, EMT, Phlebotomist, or any other
          state-licensed profession.
        </p>
        <p>
          Students seeking to practice in a licensed capacity must contact their
          state&apos;s licensing agency directly, complete that agency&apos;s
          required application, and pass the state-administered or
          nationally-administered licensure examination (e.g., NCLEX-PN, NCLEX-RN,
          state-board CMA exam, EMT National Registry exam, NHA / ASCP phlebotomy
          exams). MedCare cannot and does not guarantee admission, eligibility, or
          passage of any state examination.
        </p>
      </>
    ),
  },
  {
    id: 'doctorate-scope',
    icon: BookOpen,
    title: 'Doctorate Programs &amp; Non-Licensed Practice',
    body: (
      <>
        <p>
          MedCare&apos;s doctorate-level programs (Doctor of Natural Health, Doctor
          of Holistic Wellness, Herbal Health Practitioner, Integrative Wellness
          Consultant, Doctor of Vital Health Sciences — Practitioner) are
          <strong> non-licensed wellness credentials</strong>.
        </p>
        <p>
          These doctorates do NOT confer the legal right to practice medicine,
          prescribe medications, diagnose disease, or perform any procedure
          reserved for state-licensed clinicians. Graduates may consult on
          lifestyle, nutrition, mind-body practices, and wellness coaching
          consistent with their training and within the legal scope of their
          jurisdiction.
        </p>
      </>
    ),
  },
  {
    id: 'medical-disclaimer',
    icon: AlertTriangle,
    title: 'Medical / Clinical Practice Disclaimer',
    body: (
      <>
        <p>
          Course content, study materials, training manuals, and AI tutor responses
          provided by MedCare are for <strong>educational purposes only</strong>.
          Nothing on this platform constitutes medical advice, diagnosis, or
          treatment. Students must never use MedCare materials to make patient-care
          decisions outside of a properly supervised, licensed clinical setting.
        </p>
        <p>
          MedCare and its instructors are not responsible for actions taken by
          students in clinical practice, employment, or independent consulting.
          Students are responsible for practicing within the scope authorized by
          their state license and their employer&apos;s policies.
        </p>
      </>
    ),
  },
  {
    id: 'clinical-affiliation',
    icon: FileText,
    title: 'Clinical Affiliation &amp; Supervised Hours',
    body: (
      <>
        <p>
          Healthcare-pathway students (CNA, PN, EMT, Phlebotomy, Medical Aide) are
          required to complete supervised clinical hours at a hospital, long-term
          care facility, or clinic of their own choosing using MedCare&apos;s
          downloadable Clinical Affiliation / Student Intern Form.
        </p>
        <p>
          MedCare does NOT place students at clinical sites and does NOT employ
          students during clinical hours. Students are responsible for securing
          their own clinical site, complying with that site&apos;s policies,
          insurance, and background-check requirements, and submitting completed
          documentation to MedCare for review. Additional requirements and
          additional charges may apply.
        </p>
      </>
    ),
  },
  {
    id: 'sales-policy',
    icon: Scale,
    title: 'No-Refund Sales Policy (Summary)',
    body: (
      <>
        <p>
          <strong>All sales are final.</strong> Due to the digital nature of our
          online courses, MedCare does not offer refunds once payment has been
          processed. By enrolling, you acknowledge and agree to the full
          {' '}<Link to="/refund-policy" style={{ color: '#0d9488', textDecoration: 'underline' }}>
            MedCare Refund Policy
          </Link>{' '}— please read it in full before purchasing.
        </p>
      </>
    ),
  },
];

const DisclaimersPage = () => (
  <div style={{ minHeight: '100vh', background: '#f8fafc', color: '#1e293b' }}>
    <div style={{ background: '#0f172a', color: 'white', padding: '40px 24px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Link to="/" data-testid="disclaimers-back-link" style={{ display: 'inline-flex', gap: 8, color: '#94a3b8', textDecoration: 'none', alignItems: 'center', marginBottom: 18 }}>
          <ArrowLeft size={18} /> Back to Home
        </Link>
        <h1 data-testid="disclaimers-page-title" style={{ fontSize: '2.4rem', margin: 0, color: 'white' }}>MedCare Disclaimers</h1>
        <p style={{ color: '#cbd5e1', maxWidth: 720, marginTop: 12, lineHeight: 1.6 }}>
          Please read every section below before enrolling in a MedCare course or
          subscribing to MedCare association membership. These disclaimers form
          part of your enrollment agreement.
        </p>
      </div>
    </div>

    <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 24px 80px' }}>
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={`disclaimer-section-${s.id}`}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              padding: 28,
              marginBottom: 18,
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: '#fef3c7', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={22} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a' }} dangerouslySetInnerHTML={{ __html: s.title }} />
            </div>
            <div style={{ color: '#334155', lineHeight: 1.7, fontSize: '0.97rem' }}>{s.body}</div>
          </section>
        );
      })}

      <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: 30 }}>
        Last updated: February 2026 · MedCare Nursing Institute &amp; Training Academy
        · 3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
      </div>
    </div>
  </div>
);

export default DisclaimersPage;
